import React, {useCallback, useEffect, useRef, useState} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {PiArrowLeftBold, PiArrowRightBold, PiArrowsInBold, PiArrowsOutBold} from "react-icons/pi";
import {TOOLTIP_POPPER_CONFIG} from "../tooltipConfig.js";
import {FolienHoeheKontext} from "./FolienHoehe.js";

/**
 * Folien-Rahmen der Erklärung "So rechnet der Bereich" (Auftrag S7, Wortlaut
 * Thorsten 22.09.2026: "im Meeting soll man sie Schritt für Schritt zeigen
 * können"). Genau eine Folie ist sichtbar, Navigation über Knöpfe, Pfeiltasten
 * (nur wenn der Folienbereich den Fokus hat) und Klick auf die Punkte unten.
 * Vollbild über die native Fullscreen-API des Containers - kein Nachbau, kein
 * Seiten-Neuladen, keine nativen Formularelemente.
 */
export default function FolienRahmen({folien}) {
    const [index, setIndex] = useState(0);
    const [vollbild, setVollbild] = useState(false);
    const containerRef = useRef(null);
    const inhaltRef = useRef(null);
    const istErsterRender = useRef(true);
    const letzterIndex = folien.length - 1;
    const [hoehe, setHoehe] = useState(null);
    const [inhaltHoehe, setInhaltHoehe] = useState(null);

    useEffect(() => {
        const el = inhaltRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return undefined;
        }
        const beobachter = new ResizeObserver(() => {
            const stil = getComputedStyle(el);
            setInhaltHoehe(el.clientHeight - parseFloat(stil.paddingTop) - parseFloat(stil.paddingBottom));
        });
        beobachter.observe(el);
        return () => beobachter.disconnect();
    }, []);

    /**
     * Präsentationsmodus (Auftrag #59, Wortlaut Thorsten 29.09.2026: „Die
     * Leiste zum Vor- und Zurückblättern soll immer unten fixiert sein“ und
     * „Der Inhalt jeder einzelnen Folie muss auf die Höhe optimiert sein“):
     * Der Rahmen ist genau so hoch wie der sichtbare Bereich des
     * scrollenden Seitencontainers (bzw. des Fensters), die Leiste steht
     * damit immer am unteren Rand, die Folie nutzt die Höhe dazwischen.
     * Gemessen wird die tatsächliche Fensterhöhe, keine feste Pixelzahl.
     */
    useEffect(() => {
        const scrollVorfahr = () => {
            let el = containerRef.current?.parentElement;
            while (el && el !== document.body) {
                const oy = getComputedStyle(el).overflowY;
                if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight) {
                    return el;
                }
                el = el.parentElement;
            }
            return null;
        };
        const messen = () => {
            const vorfahr = scrollVorfahr();
            const sichtbar = vorfahr ? vorfahr.clientHeight : window.innerHeight;
            setHoehe(Math.max(420, sichtbar - 16));
        };
        messen();
        window.addEventListener("resize", messen);
        return () => window.removeEventListener("resize", messen);
    }, []);

    /**
     * Holt beim Folienwechsel den Folienkopf in den Blick (Befund S12,
     * Sichtprüfung 3): bei den hohen Folien blieb sonst die Scrollposition
     * stehen und man landete mitten im Schaubild. Die Seite selbst scrollt
     * nicht - gescrollt wird der innere Container der Layout-Seite
     * (`div.flex-grow-1.position-relative.overflow-auto`); `scrollIntoView`
     * findet ihn automatisch als nächsten scrollbaren Vorfahren.
     * Beim allerersten Rendern wird nicht gescrollt, um die Seite beim
     * Laden nicht ungefragt zu verschieben.
     */
    useEffect(() => {
        if (istErsterRender.current) {
            istErsterRender.current = false;
            return;
        }
        if (inhaltRef.current) {
            inhaltRef.current.scrollTop = 0;
        }
        containerRef.current?.scrollIntoView({block: "start"});
    }, [index]);

    const gehe = useCallback(
        (zielIndex) => setIndex((i) => Math.max(0, Math.min(letzterIndex, typeof zielIndex === "function" ? zielIndex(i) : zielIndex))),
        [letzterIndex]
    );

    useEffect(() => {
        const beimFullscreenWechsel = () => setVollbild(document.fullscreenElement === containerRef.current);
        document.addEventListener("fullscreenchange", beimFullscreenWechsel);
        return () => document.removeEventListener("fullscreenchange", beimFullscreenWechsel);
    }, []);

    const vollbildUmschalten = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            containerRef.current?.requestFullscreen?.();
        }
    };

    const beiTastendruck = (e) => {
        if (e.key === "ArrowRight" || e.key === "PageDown") {
            e.preventDefault();
            gehe((i) => i + 1);
        } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
            e.preventDefault();
            gehe((i) => i - 1);
        } else if (e.key === "Escape" && document.fullscreenElement === containerRef.current) {
            document.exitFullscreen?.();
        }
    };

    const aktuelleFolie = folien[index];

    return (
        <div
            ref={containerRef}
            className={`cosmos-erklaerung-rahmen border rounded-2 bg-white d-flex flex-column ${vollbild ? "cosmos-erklaerung-vollbild" : ""}`}
            style={{height: vollbild ? "100vh" : hoehe ?? undefined}}
            tabIndex={0}
            role="group"
            aria-roledescription="Folienpräsentation"
            aria-label={`Folie ${index + 1} von ${folien.length}: ${aktuelleFolie.titel}`}
            onKeyDown={beiTastendruck}
        >
            <div ref={inhaltRef} className="p-3 p-md-4 flex-grow-1" style={{minHeight: 0, overflowY: "auto"}}>
                <FolienHoeheKontext.Provider value={{inhalt: inhaltHoehe, grafik: null, nummer: index + 1}}>{aktuelleFolie.inhalt}</FolienHoeheKontext.Provider>
            </div>

            <div className="d-flex align-items-center justify-content-between gap-3 border-top px-3 py-2 bg-body-secondary flex-wrap flex-shrink-0">
                <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-erklaerung-zurueck">Eine Folie zurück (Pfeiltaste links).</Tooltip>}>
                    <span>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={index === 0} onClick={() => gehe((i) => i - 1)}>
                            <PiArrowLeftBold aria-hidden="true" /> Zurück
                        </button>
                    </span>
                </OverlayTrigger>

                <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">
                        Folie {index + 1} / {folien.length}
                    </span>
                    <div className="d-flex gap-1" role="tablist" aria-label="Zu einer Folie springen">
                        {folien.map((f, i) => (
                            <OverlayTrigger key={f.id} flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-erklaerung-punkt-${f.id}`}>{f.titel}</Tooltip>}>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={i === index}
                                    aria-label={`Folie ${i + 1}: ${f.titel}`}
                                    className="btn p-0 border-0"
                                    style={{width: 10, height: 10, borderRadius: "50%", backgroundColor: i === index ? "#a3d900" : "#dee2e6"}}
                                    onClick={() => gehe(i)}
                                />
                            </OverlayTrigger>
                        ))}
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-erklaerung-vollbild">{vollbild ? "Vollbild verlassen." : "Im Meeting groß zeigen – Vollbild öffnen."}</Tooltip>}>
                        <span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                aria-label={vollbild ? "Vollbild verlassen" : "Vollbild öffnen"}
                                onClick={vollbildUmschalten}
                            >
                                {vollbild ? <PiArrowsInBold aria-hidden="true" /> : <PiArrowsOutBold aria-hidden="true" />}
                            </button>
                        </span>
                    </OverlayTrigger>
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-erklaerung-weiter">Eine Folie weiter (Pfeiltaste rechts).</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-sm btn-primary" disabled={index === letzterIndex} onClick={() => gehe((i) => i + 1)}>
                                Weiter <PiArrowRightBold aria-hidden="true" />
                            </button>
                        </span>
                    </OverlayTrigger>
                </div>
            </div>
        </div>
    );
}
